import { useCallback, useEffect, useRef, useState } from 'react';

interface EmailVerificationToastProps {
  show: boolean;
  onClose: () => void;
}

export default function EmailVerificationToast({ show, onClose }: EmailVerificationToastProps) {
  const [isVisible, setIsVisible] = useState(false);

  // Keep the latest onClose in a ref so handleClose has a stable identity
  // and doesn't restart the auto-dismiss timer whenever the parent re-renders.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => onCloseRef.current(), 300);
  }, []);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [show, handleClose]);

  if (!show) return null;

  return (
    <div className={`fixed top-5 right-5 z-[995] max-w-sm transition-all duration-300 ease-in-out ${
      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
    }`}>
      <div className="bg-green-500 text-white p-4 rounded-lg shadow-lg flex items-start gap-3">
        <div className="flex-shrink-0 w-6 h-6 mt-0.5">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </div>
        <div className="flex-1">
          <div className="font-semibold text-sm mb-1">Email verified successfully!</div>
          <p className="text-sm opacity-90 leading-5">Your email has been verified and you can now receive alerts.</p>
        </div>
        <button 
          onClick={handleClose} 
          className="flex-shrink-0 p-1 rounded hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50 transition-colors"
          aria-label="Close notification"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>
  );
}